import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Megaphone, LayoutTemplate, Layers, Crown, MapPin, GraduationCap, Check, Loader2, X, Utensils, Music, Scissors, Dumbbell, Briefcase, Cpu, BookOpen, Activity } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Food & Beverage', 'Activity', 'Night Life', 'Hair & Beauty', 'Body & Wellness', 'Services'];

const BUSINESS_TYPES = [
  { icon: Utensils, label: 'Food & Beverage' },
  { icon: Activity, label: 'Activity' },
  { icon: Music, label: 'Night Life' },
  { icon: Scissors, label: 'Hair & Beauty' },
  { icon: Dumbbell, label: 'Body & Wellness / Fitness' },
  { icon: Briefcase, label: 'Services' },
  { icon: Cpu, label: 'Tech' },
  { icon: BookOpen, label: 'Education' },
];

const TIERS = [
  {
    id: 'starter-colleges',
    name: 'FltrMax Starter for Colleges',
    price: '$500',
    period: '/mo',
    highlight: true,
    badge: 'FEATURED',
    tagline: 'Reach the college community around your business',
    limit: 'Limited to 50 businesses per city',
    features: [
      'Banner Advertising',
      'Swipe Card Advertising',
      'FltrLite Pro Subscription Included',
      'Ads shown to users whose college is in your city',
      'Ads shown to users located in your city',
    ],
    accent: 'purple',
  },
  {
    id: 'starter',
    name: 'FltrMax Starter',
    price: '$1.5K',
    period: '/mo',
    tagline: 'Advertise to everyone in your city',
    features: [
      'Banner Advertising',
      'Swipe Card Advertising',
      'FltrLite Pro Subscription Included',
      'Visible in the algorithm of users in your city',
      'Choose visibility across 6 categories',
    ],
    accent: 'blue',
  },
  {
    id: 'basic',
    name: 'FltrMax Basic',
    price: '$4K',
    period: '/mo',
    tagline: 'Advertise across your entire state',
    features: [
      'Banner Advertising',
      'Swipe Card Advertising',
      'FltrLite Pro Subscription Included',
      'Visible in the algorithm of users in your state',
      'Choose visibility across 6 categories',
    ],
    accent: 'amber',
  },
];

const accentMap = {
  purple: { border: 'border-purple-300', ring: 'hover:border-purple-500', head: 'from-purple-600 to-pink-600', chip: 'bg-purple-100 text-purple-700', check: 'text-purple-600', btn: 'bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700' },
  blue: { border: 'border-blue-200', ring: 'hover:border-blue-500', head: 'from-blue-600 to-blue-700', chip: 'bg-blue-100 text-blue-700', check: 'text-blue-600', btn: 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800' },
  amber: { border: 'border-amber-200', ring: 'hover:border-amber-500', head: 'from-amber-500 to-orange-600', chip: 'bg-amber-100 text-amber-700', check: 'text-amber-600', btn: 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700' },
};

const FltrMax = () => {
  const [modalTier, setModalTier] = useState(null);
  const [form, setForm] = useState({ business_name: '', city: '', state: '', email: '' });
  const [sending, setSending] = useState(false);
  const [status, setStatus] = useState(null);

  const openModal = (tierName) => {
    setModalTier(tierName);
    setForm({ business_name: '', city: '', state: '', email: '' });
    setStatus(null);
  };

  const closeModal = () => {
    setModalTier(null);
    setStatus(null);
  };

  const handleSend = async () => {
    if (!form.business_name.trim() || !form.city.trim() || !form.state.trim() || !form.email.trim()) return;
    setSending(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_URL}/api/fltrmax/media-kit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tier: modalTier }),
      });
      if (!res.ok) throw new Error('Failed');
      setStatus('success');
    } catch (e) {
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="fltrmax-page">
      {/* Nav */}
      <nav className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-purple-600 transition-colors" data-testid="back-home-link">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <h2 className="text-2xl font-black text-gray-900">FltrLnk</h2>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="py-20 px-4 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 px-6 py-2 rounded-full mb-6 text-sm font-bold">
            <Megaphone className="w-4 h-4" />
            IN-APP ADVERTISING
          </div>
          <h1 className="text-5xl lg:text-6xl font-black mb-6">
            Fltr<span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-pink-300">Max</span>
          </h1>
          <p className="text-xl text-purple-100 max-w-3xl mx-auto">
            FltrMax is FltrLnk's in-app advertising platform. Advertise your business throughout FltrLnk with campaigns available at monthly or extended lengths — priced based on your campaign — to reach the right users, right where they're discovering.
          </p>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">How FltrMax Works</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Your ads appear natively across FltrLnk — as banners and swipe cards — and surface inside the discovery algorithms so nearby, relevant users find your business. Run a campaign for a single month or extend it for as long as you want to grow.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-purple-50 rounded-2xl p-6 border border-purple-100 text-center">
              <div className="w-14 h-14 bg-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><LayoutTemplate className="w-7 h-7 text-white" /></div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Banner Advertising</h3>
              <p className="text-gray-600 text-sm">Eye-catching banners placed throughout the FltrLnk experience.</p>
            </div>
            <div className="bg-pink-50 rounded-2xl p-6 border border-pink-100 text-center">
              <div className="w-14 h-14 bg-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><Layers className="w-7 h-7 text-white" /></div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Swipe Card Advertising</h3>
              <p className="text-gray-600 text-sm">Show up as a swipe card users see while discovering on the FltrScreen.</p>
            </div>
            <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 text-center">
              <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4"><MapPin className="w-7 h-7 text-white" /></div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Algorithmic Targeting</h3>
              <p className="text-gray-600 text-sm">Get placed in front of users by location — and by the categories you choose.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Who can advertise */}
      <section className="py-16 px-4 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl lg:text-4xl font-black text-gray-900 mb-4">Who Can Advertise</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              FltrMax works for all kinds of businesses — including, but not limited to:
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {BUSINESS_TYPES.map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 hover:border-purple-300 transition-colors text-center">
                  <Icon className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-800">{b.label}</p>
                </div>
              );
            })}
          </div>
          <p className="text-center text-gray-500 mt-6 text-sm italic">...and many more.</p>
        </div>
      </section>

      {/* Tiers */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-4xl lg:text-5xl font-black text-gray-900 mb-4">FltrMax Advertising Plans</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Choose the campaign reach that fits your business. Request a Media Kit for full details and to get started.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
            {TIERS.map((tier) => {
              const a = accentMap[tier.accent];
              return (
                <div
                  key={tier.id}
                  data-testid={`fltrmax-tier-${tier.id}`}
                  className={`relative bg-white rounded-3xl shadow-xl overflow-hidden border-2 ${a.border} ${a.ring} transition-colors flex flex-col ${tier.highlight ? 'lg:scale-105 shadow-2xl' : ''}`}
                >
                  {tier.badge && (
                    <div className="absolute top-4 right-4 bg-white/90 text-purple-700 px-3 py-1 rounded-full text-xs font-black z-10">{tier.badge}</div>
                  )}
                  <div className={`bg-gradient-to-r ${a.head} p-6 text-white`}>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                        {tier.id === 'starter-colleges' ? <GraduationCap className="w-6 h-6" /> : tier.id === 'basic' ? <Crown className="w-6 h-6" /> : <Megaphone className="w-6 h-6" />}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold leading-tight">{tier.name}</h3>
                      </div>
                    </div>
                    <p className="text-white/80 text-sm">{tier.tagline}</p>
                  </div>

                  <div className="p-6 flex flex-col flex-1">
                    <div className="text-center mb-6">
                      <div className="text-4xl font-black text-gray-900">{tier.price}<span className="text-lg font-semibold text-gray-500">{tier.period}</span></div>
                      {tier.limit && (
                        <div className={`inline-block ${a.chip} px-4 py-1 rounded-full text-xs font-bold mt-3`}>{tier.limit}</div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-6 flex-1">
                      {tier.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <Check className={`w-5 h-5 ${a.check} flex-shrink-0 mt-0.5`} />
                          <span className="text-gray-700 text-sm">{f}</span>
                        </li>
                      ))}
                    </ul>

                    {(tier.id === 'starter-colleges' || tier.id === 'starter' || tier.id === 'basic') && (
                      <div className="mb-6">
                        <p className="text-xs font-bold text-gray-500 uppercase mb-2">Choose from 6 categories</p>
                        <div className="flex flex-wrap gap-2">
                          {CATEGORIES.map((c) => (
                            <span key={c} className="bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full text-xs font-semibold">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}

                    <button
                      onClick={() => openModal(tier.name)}
                      data-testid={`get-media-kit-${tier.id}`}
                      className={`w-full ${a.btn} text-white py-3.5 rounded-xl font-bold transition-all transform hover:scale-[1.02] shadow-lg`}
                    >
                      Get Media Kit
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Media Kit Modal */}
      {modalTier && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="media-kit-modal">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative">
            <button onClick={closeModal} className="absolute top-4 right-4 text-gray-400 hover:text-gray-700" data-testid="close-modal-button">
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-black text-gray-900 mb-1">Request Media Kit</h3>
            <p className="text-purple-600 font-semibold text-sm mb-6">{modalTier}</p>

            {status === 'success' ? (
              <div className="text-center py-6" data-testid="media-kit-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-lg font-bold text-gray-900 mb-2">Request Sent!</p>
                <p className="text-gray-600 text-sm mb-6">Our team will send your Media Kit shortly.</p>
                <button onClick={closeModal} className="bg-purple-600 text-white px-6 py-2.5 rounded-xl font-bold hover:bg-purple-700 transition-colors">Close</button>
              </div>
            ) : (
              <>
                {status === 'error' && (
                  <div className="mb-4 p-3 rounded-xl bg-red-100 text-red-800 text-sm text-center" data-testid="media-kit-error">
                    Something went wrong. Please try again.
                  </div>
                )}
                <div className="space-y-3">
                  <input type="text" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} placeholder="Business Name" data-testid="mk-business-name" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors" />
                  <input type="text" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="Business City" data-testid="mk-city" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors" />
                  <input type="text" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Business State" data-testid="mk-state" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors" />
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Business Email" data-testid="mk-email" className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-purple-500 focus:outline-none transition-colors" />
                </div>
                <button
                  onClick={handleSend}
                  disabled={sending || !form.business_name.trim() || !form.city.trim() || !form.state.trim() || !form.email.trim()}
                  data-testid="mk-send-button"
                  className="w-full mt-5 bg-gradient-to-r from-purple-600 to-pink-600 text-white py-3.5 rounded-xl font-bold hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {sending ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : 'Send'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-10 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-2xl font-black mb-2">FltrLnk</h2>
          <p className="text-gray-400 text-sm">FltrMax In-App Advertising</p>
        </div>
      </footer>
    </div>
  );
};

export default FltrMax;
